import { FlexColumn, FlexRow, LabeledData, MaterialIcon, useLoading } from '@gorila-shared-ui/components';
import { Button } from 'baseui/button';
import { Input } from 'baseui/input';
import { LabelMedium, LabelSmall } from 'baseui/typography';
import { memo, useEffect, useState } from 'react';
import { areEqual } from 'react-window';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { FEEDBACK, FEEDBACK_PREFIXES } from '../../constants/app';
import { ASSET_TYPE } from '../../constants/asset';
import { useFeedback } from '../../hooks/useFeedback';
import { useStyles } from '../../hooks/useStyles';
import { updateAsset } from '../../services/assetService';
import { apiParamsState, isAllWorkspacesSelect } from '../../storage/app';
import { updateCurrentStatesState } from '../../storage/client';
import { AssetCore } from '../../types/asset';
import { capitalize } from '../../utils/strings';
import AssetTypeIcon from '../map/markers/AssetTypeIcon';
import { StyledTooltip } from '../ui/StyledTooltip';

type Props = {
  asset: AssetCore;
  ident?: string;
  enabledLabelEdit?: boolean;
};

function AssetData({ asset, ident, enabledLabelEdit = false }: Readonly<Props>) {
  const { theme, classes, css } = useStyles();
  const [editingAsset, setEditingAsset] = useState<AssetCore>();
  const [editMode, setEditMode] = useState(false);
  const { loading, startLoading, stopLoading } = useLoading();
  const { showFailFeedback, showPositiveFeedback } = useFeedback();
  const setUpdateCurrentStates = useSetRecoilState(updateCurrentStatesState);
  const apiParams = useRecoilValue(apiParamsState);
  const isAllWorkspacesSelected = useRecoilValue(isAllWorkspacesSelect);

  const canSubmit = !!editingAsset?.label;
  const hasUpdates = !areEqual(asset || {}, editingAsset || {});

  useEffect(() => {
    if (!enabledLabelEdit) return;
    setEditingAsset(asset);
  }, [enabledLabelEdit, asset]);

  const onInputChange = (value: string | null | undefined, field: keyof AssetCore) => {
    setEditingAsset((prev) => ({ ...prev!, [field]: value }));
  };

  const onCancel = () => {
    setEditMode(false);
    setEditingAsset(asset);
  };

  const onSave = async () => {
    startLoading();
    if (!apiParams || !editingAsset || !editingAsset.subClient) return;
    const { id, error } = await updateAsset(apiParams.clientId, editingAsset.subClient?._id, editingAsset);
    if (!error && id) {
      showPositiveFeedback(FEEDBACK.edited(FEEDBACK_PREFIXES.asset, editingAsset?.label));
      setUpdateCurrentStates((prev) => prev + 1);
    } else {
      showFailFeedback(error || FEEDBACK.failedEdition(FEEDBACK_PREFIXES.asset, asset?.label));
    }
    setEditMode(false);
    stopLoading();
  };

  return (
    <FlexColumn gap={theme.sizing.scale200}>
      <LabelMedium className={classes.dataHeaderClass}>Datos del activo</LabelMedium>
      <FlexColumn
        gap={theme.sizing.scale200}
        classNames={classes.dataContentClass}
      >
        {isAllWorkspacesSelected && (
          <LabeledData
            label="Subcliente:"
            content={asset.subClient?.name}
          />
        )}
        {ident && (
          <LabeledData
            label="Dispositivo:"
            content={ident}
          />
        )}
        {!editMode && (
          <FlexRow classNames={css({ alignItems: 'center' })}>
            <LabeledData
              label="Etiqueta:"
              content={asset.label}
            />
            {enabledLabelEdit && (
              <StyledTooltip
                content={'Editar etiqueta'}
                showArrow={false}
              >
                <Button
                  size="mini"
                  shape="square"
                  onClick={() => setEditMode(true)}
                  kind="tertiary"
                >
                  <MaterialIcon
                    size="mini"
                    name={'edit'}
                  />
                </Button>
              </StyledTooltip>
            )}
          </FlexRow>
        )}
        {editMode && (
          <FlexRow classNames={css({ alignItems: 'center' })}>
            <LabelSmall>Etiqueta:</LabelSmall>
            <Input
              value={editingAsset?.label}
              size="mini"
              onChange={(event) => onInputChange(event.target.value, 'label')}
            />
            <FlexRow gap={0}>
              <StyledTooltip
                content={'Cancelar'}
                showArrow={false}
              >
                <Button
                  size="mini"
                  shape="square"
                  onClick={onCancel}
                  kind="tertiary"
                >
                  <MaterialIcon
                    size="mini"
                    name={'cancel'}
                  />
                </Button>
              </StyledTooltip>
              <StyledTooltip
                content={'Guardar'}
                showArrow={false}
              >
                <Button
                  size="mini"
                  shape="square"
                  onClick={onSave}
                  kind="tertiary"
                  isLoading={loading}
                  disabled={!canSubmit || !hasUpdates}
                >
                  <MaterialIcon
                    size="mini"
                    name={'done'}
                  />
                </Button>
              </StyledTooltip>
            </FlexRow>
          </FlexRow>
        )}
        <LabeledData
          label="Tipo:"
          verticalAlign="center"
          content={
            <StyledTooltip
              content={capitalize(ASSET_TYPE[asset.assetType] ?? '')}
              showArrow={false}
              placement="top"
            >
              <AssetTypeIcon assetType={asset.assetType} />
            </StyledTooltip>
          }
        />
      </FlexColumn>
    </FlexColumn>
  );
}

export default memo(AssetData);
